import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Circle, Play, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

interface Process {
  id: string;
  name: string;
  code: string | null;
  sequence_order: number;
}

interface ProcessRecord {
  id: string;
  process_id: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  operator_id: string | null;
  operator_name?: string | null;
}

interface Props {
  workOrderId: string | null | undefined;
}

const friendlyName = (n: string) => {
  const map: Record<string, string> = {
    material_arrival: 'Llegada material',
    material_cutting: 'Corte material',
    freezer_storage: 'Congelador',
    mold_selection: 'Selección molde',
    material_layup: 'Disposición material',
    vacuum_bagging: 'Vacuum bagging',
    autoclave_curing: 'Curado autoclave',
    demolding: 'Desmolde',
    quality_inspection: 'Control calidad',
    cutting_workshop: 'Taller corte',
    packaging: 'Embalaje',
    dispatch: 'Despacho',
  };
  return map[n] || n;
};

export function ProcessTimeline({ workOrderId }: Props) {
  const { data: processes } = useQuery({
    queryKey: ['mfg-processes-all'],
    queryFn: async () => {
      const { data, error } = await api
        .from('manufacturing_processes')
        .select('id, name, code, sequence_order')
        .order('sequence_order');
      if (error) throw error;
      return (data || []) as Process[];
    },
  });

  const { data: records } = useQuery({
    queryKey: ['process-records-wo', workOrderId],
    queryFn: async () => {
      if (!workOrderId) return [];
      const { data, error } = await api
        .from('process_records')
        .select('id, process_id, status, started_at, completed_at, operator_id')
        .eq('work_order_id', workOrderId)
        .order('started_at', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data || []) as ProcessRecord[];
    },
    enabled: !!workOrderId,
  });

  if (!workOrderId) {
    return <p className="text-xs text-muted-foreground italic">Chip sin orden de trabajo asociada</p>;
  }
  if (!processes || processes.length === 0) {
    return <p className="text-xs text-muted-foreground">Cargando flujograma...</p>;
  }

  // Map process_id → record (latest if multiple)
  const recByProcess: Record<string, ProcessRecord> = {};
  for (const r of records || []) {
    const existing = recByProcess[r.process_id];
    if (!existing) {
      recByProcess[r.process_id] = r;
    } else if (r.completed_at && (!existing.completed_at || r.completed_at > existing.completed_at)) {
      recByProcess[r.process_id] = r;
    }
  }

  const stateOf = (procId: string) => {
    const r = recByProcess[procId];
    if (!r) return 'pending';
    if (r.status === 'completed') return 'completed';
    if (r.status === 'in_progress') return 'in_progress';
    if (r.status === 'paused') return 'paused';
    return 'pending';
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Flujograma (14 pasos)</p>
      <div className="space-y-1">
        {processes.map((p) => {
          const state = stateOf(p.id);
          const r = recByProcess[p.id];
          const Icon =
            state === 'completed' ? CheckCircle2 : state === 'in_progress' ? Play : state === 'paused' ? AlertCircle : Circle;
          const colorCls =
            state === 'completed'
              ? 'text-green-600 bg-green-50 border-green-200'
              : state === 'in_progress'
              ? 'text-blue-600 bg-blue-50 border-blue-200'
              : state === 'paused'
              ? 'text-amber-600 bg-amber-50 border-amber-200'
              : 'text-muted-foreground bg-muted/30 border-border';
          return (
            <div key={p.id} className={`flex items-center gap-2 px-2 py-1.5 rounded border text-xs ${colorCls}`}>
              <span className="font-mono w-5 text-right">{p.sequence_order}.</span>
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 truncate">{friendlyName(p.name)}</span>
              {r?.completed_at && (
                <span className="text-[10px] opacity-75">{format(new Date(r.completed_at), 'dd/MM HH:mm')}</span>
              )}
              {state === 'in_progress' && <Badge variant="outline" className="text-[10px]">en curso</Badge>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
